/**
 * Centralized Security Configuration
 * 
 * Encryption parameters, JWT settings, rate limits, and other security-related
 * configuration used across all packages.
 */

import type { Environment } from './environments';

export interface CryptoConfig {
  // AES-GCM Configuration
  aes: {
    algorithm: 'AES-GCM';
    keyLength: 256; // bits
    ivLength: 12; // bytes for GCM
    tagLength: 16; // bytes for GCM authentication tag
  };
  
  // PBKDF2 Configuration
  pbkdf2: {
    iterations: number;
    saltLength: 32; // bytes
    keyLength: 32; // bytes (256 bits)
    hashAlgorithm: 'SHA-256';
  };
  
  // Vault ID derivation
  vaultId: {
    hashAlgorithm: 'SHA-256';
    encoding: 'hex';
  };
}

export interface JWTConfig {
  // Token configuration
  accessToken: {
    expiresIn: string; // e.g., '15m', '1h', '1d'
    algorithm: 'HS256';
    issuer: string;
    audience: string;
  };
  
  refreshToken: {
    expiresIn: string; // e.g., '7d', '30d'
    algorithm: 'HS256';
    issuer: string;
    audience: string;
  };
  
  // Security settings
  security: {
    minSecretLength: number; // minimum JWT secret length in characters
    rotateRefreshTokens: boolean;
    revokeRefreshTokenOnReuse: boolean;
  };
}

export interface PasswordConfig {
  // Password hashing (bcrypt)
  hashing: {
    algorithm: 'bcrypt';
    rounds: number;
  };
  
  // Password requirements
  requirements: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSymbols: boolean;
    maxLength: number;
  };
  
  // Password validation regex
  validation: {
    pattern: string;
    message: string;
  };
}

export interface SessionConfig {
  // Session timeouts (minutes)
  timeouts: {
    free: number; // Free tier session timeout
    premium: number; // Premium tier session timeout
    development: number; // Development environment
  };
  
  // Auto-lock settings
  autoLock: {
    enabled: boolean;
    idleTimeMinutes: number;
    onWindowBlur: boolean;
    onSystemSleep: boolean;
  };
  
  // Session storage
  storage: {
    maxSessions: number; // Maximum concurrent sessions per user
    cleanupInterval: number; // Minutes between cleanup runs
  };
}

export interface SecurityHeaders {
  // CORS settings
  cors: {
    maxAge: number; // seconds
    credentials: boolean;
    optionsSuccessStatus: number;
  };
  
  // Security headers
  headers: {
    contentSecurityPolicy: string;
    xFrameOptions: string;
    xContentTypeOptions: string;
    referrerPolicy: string;
    strictTransportSecurity: string;
  };
}

export interface SecurityConfig {
  crypto: CryptoConfig;
  jwt: JWTConfig;
  password: PasswordConfig;
  session: SessionConfig;
  headers: SecurityHeaders;
}

// Environment-specific security configurations
const SECURITY_CONFIGS: Record<Environment, SecurityConfig> = {
  local: {
    crypto: {
      aes: {
        algorithm: 'AES-GCM',
        keyLength: 256,
        ivLength: 12,
        tagLength: 16,
      },
      pbkdf2: {
        iterations: 310000, // OWASP recommended minimum
        saltLength: 32,
        keyLength: 32,
        hashAlgorithm: 'SHA-256',
      },
      vaultId: {
        hashAlgorithm: 'SHA-256',
        encoding: 'hex',
      },
    },
    
    jwt: {
      accessToken: {
        expiresIn: '24h', // Longer for development
        algorithm: 'HS256',
        issuer: 'safermark-local',
        audience: 'safermark-users',
      },
      refreshToken: {
        expiresIn: '30d',
        algorithm: 'HS256',
        issuer: 'safermark-local',
        audience: 'safermark-users',
      },
      security: {
        minSecretLength: 32,
        rotateRefreshTokens: true,
        revokeRefreshTokenOnReuse: true,
      },
    },
    
    password: {
      hashing: {
        algorithm: 'bcrypt',
        rounds: 10, // Lower for faster development
      },
      requirements: {
        minLength: 8,
        requireUppercase: false, // Relaxed for development
        requireLowercase: true,
        requireNumbers: false, // Relaxed for development
        requireSymbols: false, // Relaxed for development
        maxLength: 128,
      },
      validation: {
        pattern: '^.{8,128}$',
        message: 'Password must be at least 8 characters long',
      },
    },
    
    session: {
      timeouts: {
        free: 0, // No timeout in local development
        premium: 0, // No timeout in local development
        development: 0,
      },
      autoLock: {
        enabled: false, // Disabled for development
        idleTimeMinutes: 60,
        onWindowBlur: false,
        onSystemSleep: false,
      },
      storage: {
        maxSessions: 10,
        cleanupInterval: 60,
      },
    },
    
    headers: {
      cors: {
        maxAge: 3600,
        credentials: true,
        optionsSuccessStatus: 200,
      },
      headers: {
        contentSecurityPolicy: "default-src 'self' 'unsafe-inline' 'unsafe-eval' *;", // Relaxed for development
        xFrameOptions: 'SAMEORIGIN',
        xContentTypeOptions: 'nosniff',
        referrerPolicy: 'origin-when-cross-origin',
        strictTransportSecurity: '', // Not needed for HTTP
      },
    },
  },
  
  dev: {
    crypto: {
      aes: {
        algorithm: 'AES-GCM',
        keyLength: 256,
        ivLength: 12,
        tagLength: 16,
      },
      pbkdf2: {
        iterations: 310000,
        saltLength: 32,
        keyLength: 32,
        hashAlgorithm: 'SHA-256',
      },
      vaultId: {
        hashAlgorithm: 'SHA-256',
        encoding: 'hex',
      },
    },
    
    jwt: {
      accessToken: {
        expiresIn: '1h',
        algorithm: 'HS256',
        issuer: 'safermark-dev',
        audience: 'safermark-users',
      },
      refreshToken: {
        expiresIn: '7d',
        algorithm: 'HS256',
        issuer: 'safermark-dev',
        audience: 'safermark-users',
      },
      security: {
        minSecretLength: 32,
        rotateRefreshTokens: true,
        revokeRefreshTokenOnReuse: true,
      },
    },
    
    password: {
      hashing: {
        algorithm: 'bcrypt',
        rounds: 12,
      },
      requirements: {
        minLength: 8,
        requireUppercase: true,
        requireLowercase: true,
        requireNumbers: true,
        requireSymbols: false,
        maxLength: 128,
      },
      validation: {
        pattern: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{8,128}$',
        message: 'Password must be at least 8 characters with uppercase, lowercase, and numbers',
      },
    },
    
    session: {
      timeouts: {
        free: 60, // 1 hour for free tier
        premium: 0, // No timeout for premium
        development: 480, // 8 hours for development
      },
      autoLock: {
        enabled: true,
        idleTimeMinutes: 30,
        onWindowBlur: false,
        onSystemSleep: true,
      },
      storage: {
        maxSessions: 5,
        cleanupInterval: 30,
      },
    },
    
    headers: {
      cors: {
        maxAge: 3600,
        credentials: true,
        optionsSuccessStatus: 200,
      },
      headers: {
        contentSecurityPolicy: "default-src 'self'; script-src 'self' 'unsafe-inline' https://js.stripe.com; connect-src 'self' https://api.stripe.com;",
        xFrameOptions: 'SAMEORIGIN',
        xContentTypeOptions: 'nosniff',
        referrerPolicy: 'origin-when-cross-origin',
        strictTransportSecurity: 'max-age=31536000; includeSubDomains',
      },
    },
  },
  
  prod: {
    crypto: {
      aes: {
        algorithm: 'AES-GCM',
        keyLength: 256,
        ivLength: 12,
        tagLength: 16,
      },
      pbkdf2: {
        iterations: 310000, // OWASP recommended minimum
        saltLength: 32,
        keyLength: 32,
        hashAlgorithm: 'SHA-256',
      },
      vaultId: {
        hashAlgorithm: 'SHA-256',
        encoding: 'hex',
      },
    },
    
    jwt: {
      accessToken: {
        expiresIn: '15m', // Short-lived for security
        algorithm: 'HS256',
        issuer: 'safermark',
        audience: 'safermark-users',
      },
      refreshToken: {
        expiresIn: '7d',
        algorithm: 'HS256',
        issuer: 'safermark',
        audience: 'safermark-users',
      },
      security: {
        minSecretLength: 64, // Longer secrets in production
        rotateRefreshTokens: true,
        revokeRefreshTokenOnReuse: true,
      },
    },
    
    password: {
      hashing: {
        algorithm: 'bcrypt',
        rounds: 14, // Higher rounds for production security
      },
      requirements: {
        minLength: 12, // Stricter requirements in production
        requireUppercase: true,
        requireLowercase: true,
        requireNumbers: true,
        requireSymbols: true,
        maxLength: 128,
      },
      validation: {
        pattern: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+\\-=\\[\\]{};\':"\\\\|,.<>\\/?]).{12,128}$',
        message: 'Password must be at least 12 characters with uppercase, lowercase, numbers, and symbols',
      },
    },
    
    session: {
      timeouts: {
        free: 60, // 1 hour for free tier
        premium: 480, // 8 hours for premium
        development: 60,
      },
      autoLock: {
        enabled: true,
        idleTimeMinutes: 15, // Stricter in production
        onWindowBlur: true,
        onSystemSleep: true,
      },
      storage: {
        maxSessions: 3,
        cleanupInterval: 15,
      },
    },
    
    headers: {
      cors: {
        maxAge: 86400, // 24 hours
        credentials: true,
        optionsSuccessStatus: 200,
      },
      headers: {
        contentSecurityPolicy: "default-src 'self'; script-src 'self' https://js.stripe.com; connect-src 'self' https://api.stripe.com; style-src 'self' 'unsafe-inline';",
        xFrameOptions: 'DENY',
        xContentTypeOptions: 'nosniff',
        referrerPolicy: 'strict-origin-when-cross-origin',
        strictTransportSecurity: 'max-age=31536000; includeSubDomains; preload',
      },
    },
  },
};

/**
 * Get security configuration for a specific environment
 */
export function getSecurityConfig(environment: Environment): SecurityConfig {
  return { ...SECURITY_CONFIGS[environment] };
}

/**
 * Get crypto configuration for a specific environment
 */
export function getCryptoConfig(environment: Environment): CryptoConfig {
  return getSecurityConfig(environment).crypto;
}

/**
 * Get JWT configuration for a specific environment
 */
export function getJWTConfig(environment: Environment): JWTConfig {
  return getSecurityConfig(environment).jwt;
}

/**
 * Get password configuration for a specific environment
 */
export function getPasswordConfig(environment: Environment): PasswordConfig {
  return getSecurityConfig(environment).password;
}

/**
 * Get session configuration for a specific environment
 */
export function getSessionConfig(environment: Environment): SessionConfig {
  return getSecurityConfig(environment).session;
}

/**
 * Get security headers configuration for a specific environment
 */
export function getSecurityHeaders(environment: Environment): SecurityHeaders {
  return getSecurityConfig(environment).headers;
}

/**
 * Validate password against requirements
 */
export function validatePassword(password: string, environment: Environment): { valid: boolean; message?: string } {
  const config = getPasswordConfig(environment);
  const regex = new RegExp(config.validation.pattern);
  
  if (!regex.test(password)) {
    return {
      valid: false,
      message: config.validation.message,
    };
  }
  
  return { valid: true };
}

/**
 * Get session timeout for user tier
 */
export function getSessionTimeout(tier: 'free' | 'premium' | 'development', environment: Environment): number {
  const config = getSessionConfig(environment);
  return config.timeouts[tier];
}

// Security constants
export const SECURITY_CONSTANTS = {
  // Maximum failed login attempts before account lockout
  MAX_LOGIN_ATTEMPTS: 5,
  
  // Account lockout duration (minutes)
  ACCOUNT_LOCKOUT_DURATION: 30,
  
  // Maximum password reset attempts per hour
  MAX_PASSWORD_RESET_ATTEMPTS: 3,
  
  // Session ID length (bytes)
  SESSION_ID_LENGTH: 32,
  
  // API key length (bytes)
  API_KEY_LENGTH: 32,
  
  // Maximum file upload size (bytes)
  MAX_FILE_UPLOAD_SIZE: 10 * 1024 * 1024, // 10MB
  
  // Request body size limits (bytes)
  MAX_REQUEST_BODY_SIZE: 1024 * 1024, // 1MB
  MAX_JSON_BODY_SIZE: 100 * 1024, // 100KB
  
} as const;

// Export configurations
export { SECURITY_CONFIGS };